"use client";

import { useEffect, useMemo, useRef, useState } from "react";

import {
  searchInternshipCatalogue,
  type IInternshipCategory,
  type IInternshipCourse,
} from "@/api/internshipData";

import {
  Field,
  INPUT_CLASSNAME,
  PRIMARY_BUTTON_CLASSNAME,
  RequiredMark,
  SECONDARY_BUTTON_CLASSNAME,
} from "./primitives";
import Spinner from "../Spinner";

/**
 * Step 2 of the Apply Now flow — the source's `CourseProjectForm` +
 * `CourseSelection` collapsed into one component, since the split existed only
 * to keep the react-hook-form wiring separate from the two controls.
 *
 * ── Why the course picker is hand-rolled ────────────────────────────────────
 * The source's picker is a shadcn `Popover` + `Command` combobox, and `cmdk` is
 * not installed here. The source project also ships an unused `CourseSelect.tsx`
 * that implements the SAME control as a plain dropdown with a search box; this
 * follows that implementation, styled to match the Command version that actually
 * renders (`bg-rose-50 h-[36px]`, chevron, truncated label).
 *
 * Validation is two required fields, so it is plain state rather than
 * react-hook-form — see StudentInfoForm for why no zod resolver is used.
 */

export interface CourseSelectionValues {
  courseCategory: string;
  courseId: string;
  course: IInternshipCourse;
}

interface CourseProjectFormProps {
  onSubmit: (data: CourseSelectionValues) => void;
  onBack: () => void;
  categories: IInternshipCategory[];
  preselectedCourse?: IInternshipCourse;
  defaultValues?: { courseCategory?: string; courseId?: string };
}

/** The source fetches up to 200 courses for the chosen category. */
const COURSE_FETCH_LIMIT = 200;
/** Sentinel id for "All" — the API takes "" for no category filter. */
const ALL_CATEGORY_ID = "1";

export default function CourseProjectForm({
  onSubmit,
  onBack,
  categories,
  preselectedCourse,
  defaultValues,
}: CourseProjectFormProps) {
  const [courseCategory, setCourseCategory] = useState(
    preselectedCourse?.category?.category_id ??
      defaultValues?.courseCategory ??
      ""
  );
  const [courseId, setCourseId] = useState(
    preselectedCourse?.course_id ?? defaultValues?.courseId ?? ""
  );
  const [courses, setCourses] = useState<IInternshipCourse[]>([]);
  const [isFetching, setIsFetching] = useState(true);
  const [errors, setErrors] = useState<{ category?: string; course?: string }>(
    {}
  );

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  /** Clearing the course on category change must not fire on first render. */
  const previousCategory = useRef<string | null>(null);

  useEffect(() => {
    if (previousCategory.current === null) {
      previousCategory.current = courseCategory;
      return;
    }
    if (previousCategory.current !== courseCategory) {
      previousCategory.current = courseCategory;
      setCourseId("");
      setSearchQuery("");
    }
  }, [courseCategory]);

  // Courses for the chosen category.
  useEffect(() => {
    if (!courseCategory) {
      setIsFetching(false);
      return;
    }

    const controller = new AbortController();
    setIsFetching(true);

    searchInternshipCatalogue(
      {
        type: "courses",
        categoryId: courseCategory === ALL_CATEGORY_ID ? "" : courseCategory,
        searchTerm: "",
        skip: 0,
        limit: COURSE_FETCH_LIMIT,
      },
      controller.signal
    ).then((result) => {
      setCourses(result.data);
      setIsFetching(false);
    });

    return () => controller.abort();
  }, [courseCategory]);

  // Close the dropdown on an outside click, as the source's popover does.
  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [isOpen]);

  const filteredCourses = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return courses;
    return courses.filter((course) =>
      course.course_name?.toLowerCase().includes(query)
    );
  }, [courses, searchQuery]);

  const selectedCourseName =
    courses.find((c) => c.course_id === courseId)?.course_name || "";

  const handleSubmit = () => {
    const nextErrors: { category?: string; course?: string } = {};
    if (!courseCategory) nextErrors.category = "Please select a course category";
    if (!courseId) nextErrors.course = "Please select a course";

    const selected = courses.find((c) => c.course_id === courseId);
    // The chosen course must be in the loaded list — the source guards the same
    // way, since the payload carries the whole course object forward.
    if (courseId && !selected) nextErrors.course = "Please select a valid course";

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || !selected) return;

    onSubmit({ courseCategory, courseId, course: selected });
  };

  return (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <div className="space-y-4">
        <Field
          label={
            <>
              Select Category <RequiredMark />
            </>
          }
          error={errors.category}
        >
          <select
            className={`${INPUT_CLASSNAME} items-center justify-between py-2 bg-rose-50`}
            value={courseCategory}
            onChange={(e) => setCourseCategory(e.target.value)}
          >
            <option value="" disabled>
              Select Category
            </option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.type}
              </option>
            ))}
          </select>
        </Field>

        {isFetching ? (
          <div className="w-full flex justify-center">
            <Spinner />
          </div>
        ) : (
          <Field
            label={
              <>
                Select Course Name <RequiredMark />
              </>
            }
            error={errors.course}
          >
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setIsOpen((open) => !open)}
                disabled={!courseCategory || courses.length === 0}
                aria-expanded={isOpen}
                className={`truncate w-full justify-between bg-rose-50 h-[36px] px-3 border border-gray-300 rounded-md flex items-center text-left text-sm disabled:cursor-not-allowed disabled:opacity-50 ${
                  courseId ? "" : "text-[#BEBEBE]"
                }`}
              >
                <span className="truncate">
                  {selectedCourseName || "Select Course Name"}
                </span>
                {/* lucide ChevronsUpDown, inlined */}
                <svg
                  className="ml-2 h-4 w-4 shrink-0 opacity-50"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="m7 15 5 5 5-5M7 9l5-5 5 5" />
                </svg>
              </button>

              {isOpen ? (
                <div className="absolute z-50 mt-1 w-full bg-[#FFFFFF] border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search courses..."
                    aria-label="Search courses"
                    className="w-full px-3 py-2 text-sm border-b border-gray-200 focus:outline-none"
                  />
                  {filteredCourses.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      No courses found.
                    </div>
                  ) : (
                    <ul>
                      {filteredCourses.map((course) => (
                        <li key={course.course_id}>
                          <button
                            type="button"
                            onClick={() => {
                              setCourseId(course.course_id);
                              setIsOpen(false);
                            }}
                            className={`flex w-full items-start gap-2 text-left cursor-pointer px-3 py-2 hover:bg-rose-100 text-sm ${
                              courseId === course.course_id
                                ? "font-medium text-rose-600"
                                : ""
                            }`}
                          >
                            {/* lucide Check — the source marks the selected row with it */}
                            <svg
                              className={`mt-0.5 h-4 w-4 shrink-0 ${
                                courseId === course.course_id
                                  ? "opacity-100"
                                  : "opacity-0"
                              }`}
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <path d="M20 6 9 17l-5-5" />
                            </svg>
                            <span className="break-words whitespace-normal">
                              {course.course_name}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : null}
            </div>
          </Field>
        )}
      </div>

      <div className="flex justify-center gap-5 mt-6">
        <button
          type="button"
          onClick={onBack}
          className={`${SECONDARY_BUTTON_CLASSNAME} text-lg`}
        >
          Back
        </button>
        <button
          type="submit"
          disabled={isFetching}
          className={`${PRIMARY_BUTTON_CLASSNAME} text-lg`}
        >
          Next
        </button>
      </div>
    </form>
  );
}
